import { graphql } from '@api/generated';
import { UserSeatLevel } from '@api/generated/graphql';
import { getClient } from '../../../api/client';
import { AuthUser } from './types';

const getCurrentUserQuery = graphql(`
  query currentUser($refreshOnly: Boolean!) {
    currentUser {
      accountId
      contactCenterId
      id
      name
      isImpersonated
      impersonatorUserId
      permissions
      contactCenter @skip(if: $refreshOnly) {
        id
        name
      }
      org {
        hasActiveContract
        id
        name
        tenants {
          id
          name
          companyName
        }
        telecomConfig @skip(if: $refreshOnly) {
          pcsBaseAddress
          rtcsBaseAddress
          sipBaseAddress
        }
      }

      contactCenterUserId @skip(if: $refreshOnly)
      email @skip(if: $refreshOnly)
      personalLine {
        brand
        description
        phoneNumber
        recordingPreference
        e911Address {
          city
          country
          latitude
          longitude
          state
          street
          unit
          zip
        }
        ringingTimeout
        sendVoicemailToEmail
        inboundRecording
        inboundAnnounceRecording
        outboundRecording
        outboundAnnounceRecording
      }
      seatLevel
      hasAcceptedCallRecordingTerms
      hasAcceptedExclusionListTerms
      username @skip(if: $refreshOnly)
    }
  }
`);

async function getCurrentUser(refreshOnly: boolean = false): Promise<AuthUser> {
  const { data, error } = await getClient().query({
    fetchPolicy: 'network-only',
    query: getCurrentUserQuery,
    variables: { refreshOnly },
  });

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Current user not found.');
  }

  const user = data.currentUser;

  // We allow outbound calls if the user has a paid seat level, a personal line phone number, and an e911 address.
  const outBoundCallsAllowed: boolean =
    user.seatLevel !== undefined &&
    user.seatLevel !== UserSeatLevel.Free &&
    (user.personalLine?.phoneNumber?.length ?? 0) > 0 &&
    !!user.personalLine?.e911Address?.street &&
    !!user.personalLine?.e911Address?.city &&
    !!user.personalLine?.e911Address?.state &&
    !!user.personalLine?.e911Address?.zip;

  return {
    id: user.id,
    name: user.name,
    isImpersonated: user.isImpersonated,
    impersonatorUserId: user.impersonatorUserId ?? undefined,
    contactCenterId: user.contactCenterId,
    contactCenterUserId: user.contactCenterUserId ?? undefined,
    org: user.org,
    accountId: user.accountId,
    webPhoneToken: undefined,
    permissions: user.permissions,
    seatLevel: user.seatLevel,
    personalLine: user.personalLine ?? undefined,
    e911Address: user.personalLine?.e911Address ?? undefined,
    outboundCallsAllowed: outBoundCallsAllowed,
    callRecording: user.personalLine?.recordingPreference ?? undefined,
    hasAcceptedCallRecordingTerms: user.hasAcceptedCallRecordingTerms,
    hasAcceptedExclusionListTerms: user.hasAcceptedExclusionListTerms,
    email: user.email ?? undefined,
  };
}

export default getCurrentUser;
